import { Button, cn } from '@cibli/shared';
import { useMicdropState } from '@micdrop/react';
import { Micdrop } from '@micdrop/web';
import { Pause, Play } from 'lucide-react';
import { ComponentProps, useMemo } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';

import StatusCircle from '@/components/organisms/StatusCircle.tsx';
import { useCVTemplateContext } from '@/features/common/contexts/cv-template.context';
import { getCountryCode } from '@/utils/getCountryCode.ts';

type StatusCircleComponentProps = ComponentProps<typeof StatusCircle>;

interface VocalBarProps {
  message?: string;
  countryCode?: string;
  showFlag?: boolean;
  flagSize?: number;
  showControls?: boolean;
  onStartVocal?: () => void;
  lastMessage?: string | null;
  statusCircleProps?: Partial<StatusCircleComponentProps>;
  className?: string;
}

export default function VocalBar({
  message,
  countryCode,
  showFlag = true,
  showControls = true,
  flagSize = 20,
  onStartVocal,
  lastMessage,
  statusCircleProps,
  className,
}: VocalBarProps) {
  const { t } = useTranslation();
  const micdropState = useMicdropState();
  const { currentLocaleCv } = useCVTemplateContext();
  const { isStarted, isPaused, isListening, isUserSpeaking } = micdropState;

  const resolvedCountryCode = useMemo(() => {
    if (countryCode) {
      return countryCode.toUpperCase();
    }

    return getCountryCode(currentLocaleCv);
  }, [countryCode, currentLocaleCv]);

  const computedMessage =
    message ?? ((isListening || isUserSpeaking) && lastMessage ? lastMessage : '');

  const shouldShowFlag = showFlag && Boolean(resolvedCountryCode);
  const shouldShowControls = showControls && Boolean(onStartVocal);

  const { size = 32, ...restStatusCircleProps } = statusCircleProps ?? {};

  const handlePauseToggle = () => {
    if (!isStarted) {
      onStartVocal?.();
      return;
    }

    if (isPaused) {
      Micdrop.resume();
    } else {
      Micdrop.pause();
    }
  };

  const buttonLabel = !isStarted || isPaused ? t('common.play') : t('common.pause');

  return (
    <aside className={cn('relative px-5 py-3 mb-16', className)} role="status" aria-live="polite">
      <div className="pointer-events-auto mx-auto flex max-w-xl items-center rounded-2xl border border-gray-200 bg-white/95 px-4 py-2 shadow-lg backdrop-blur">
        <StatusCircle size={size} micdropState={micdropState} {...restStatusCircleProps} />
        {shouldShowFlag && resolvedCountryCode && (
          <ReactCountryFlag
            countryCode={resolvedCountryCode}
            svg
            style={{
              width: flagSize,
              height: flagSize,
              borderRadius: '50%',
              objectFit: 'cover',
            }}
          />
        )}
        <p className="truncate text-sm text-gray-700">{computedMessage}</p>
        {shouldShowControls && (
          <Button
            variant="secondary"
            size="icon"
            onClick={handlePauseToggle}
            aria-label={buttonLabel}
            className={cn(
              'w-8 h-8 flex-shrink-0',
              isPaused && 'bg-primary text-white hover:bg-primary/80',
              !isPaused && 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            )}
          >
            {!isStarted || isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </Button>
        )}
      </div>
    </aside>
  );
}
